import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './context/CartContext';
import { ProductProvider } from './context/ProductContext';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Loader from './components/Loader';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

function lazyChecked<T extends { default: React.ComponentType<any> }>(
  importer: () => Promise<T>,
  name: string
) {
  return React.lazy(async () => {
    const mod: any = await importer();
    if (!mod?.default) {
      // This is the exact cause of: "Lazy element type must resolve to a class or function"
      console.error(
        `[lazyChecked] Module "${name}" has no default export. Exports:`,
        Object.keys(mod || {})
      );
      throw new Error(`Lazy module "${name}" has no default export.`);
    }
    return mod as T;
  });
}

// Route-based code splitting
const Home = lazyChecked(() => import('./pages/Home'), 'pages/Home');
const Shop = lazyChecked(() => import('./pages/Shop'), 'pages/Shop');
const ProductDetail = lazyChecked(() => import('./pages/ProductDetail'), 'pages/ProductDetail');
const Cart = lazyChecked(() => import('./pages/Cart'), 'pages/Cart');
const Checkout = lazyChecked(() => import('./pages/Checkout'), 'pages/Checkout');
const ThankYou = lazyChecked(() => import('./pages/ThankYou'), 'pages/ThankYou');
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in Route path="/track-order"
const TrackOrder = lazyChecked(() => import('./pages/TrackOrder'), 'pages/TrackOrder');
const About = lazyChecked(() => import('./pages/About'), 'pages/About');
const Contact = lazyChecked(() => import('./pages/Contact'), 'pages/Contact');
const Shipping = lazyChecked(() => import('./pages/Shipping'), 'pages/Shipping');
const Returns = lazyChecked(() => import('./pages/Returns'), 'pages/Returns');
const FAQ = lazyChecked(() => import('./pages/FAQ'), 'pages/FAQ');
const Portfolio = lazyChecked(() => import('./pages/Portfolio'), 'pages/Portfolio');
const Login = lazyChecked(() => import('./pages/Login'), 'pages/Login');

// Admin routes
const AdminDashboard = lazyChecked(() => import('./admin/AdminDashboard'), 'admin/AdminDashboard');
const AddProduct = lazyChecked(() => import('./admin/AddProduct'), 'admin/AddProduct');
const EditProduct = lazyChecked(() => import('./admin/EditProduct'), 'admin/EditProduct');
const ManageProducts = lazyChecked(() => import('./admin/ManageProducts'), 'admin/ManageProducts');
const ManageCategories = lazyChecked(() => import('./admin/ManageCategories'), 'admin/ManageCategories');
const ManageOrders = lazyChecked(() => import('./admin/ManageOrders'), 'admin/ManageOrders');
const CustomerAnalytics = lazyChecked(() => import('./admin/CustomerAnalytics'), 'admin/CustomerAnalytics');
const InventoryAlerts = lazyChecked(() => import('./admin/InventoryAlerts'), 'admin/InventoryAlerts');
const CustomerMessages = lazyChecked(() => import('./admin/CustomerMessages'), 'admin/CustomerMessages');
const PromotionalBanners = lazyChecked(() => import('./admin/PromotionalBanners'), 'admin/PromotionalBanners');

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ProductProvider>
          <CartProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <ErrorBoundary>
                <Suspense fallback={<Loader message="Loading..." />}>
                  <div className="App">
                    <Navbar />
                    <main className="pt-20">
                      <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<Home />} />
                        <Route path="/shop" element={<Shop />} />
                        <Route path="/product/:id" element={<ProductDetail />} />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/thank-you" element={<ThankYou />} />
                        <Route path="/track-order" element={<TrackOrder />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/shipping" element={<Shipping />} />
                        <Route path="/returns" element={<Returns />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/portfolio" element={<Portfolio />} />
                        <Route path="/login" element={<Login />} />
                        
                        {/* Protected Admin routes */}
                        <Route 
                          path="/admin" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminDashboard />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/admin/add-product" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <AddProduct />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/admin/edit-product/:id" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <EditProduct />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/admin/manage-products" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <ManageProducts />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/admin/categories" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <ManageCategories />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/admin/orders" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <ManageOrders />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/admin/customer-analytics" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <CustomerAnalytics />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/admin/inventory-alerts" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <InventoryAlerts />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/admin/customer-messages" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <CustomerMessages />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/admin/promotional-banners" 
                          element={
                            <ProtectedRoute requireAdmin>
                              <PromotionalBanners />
                            </ProtectedRoute>
                          } 
                        />
                      </Routes>
                    </main>
                    <Footer />
                  </div>
                </Suspense>
              </ErrorBoundary>
            </Router>
          </CartProvider>
        </ProductProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
